import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, isDevMode, provideZoneChangeDetection } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideTransloco, TRANSLOCO_MISSING_HANDLER } from '@jsverse/transloco';
import { provideTranslocoLocale } from '@jsverse/transloco-locale';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { provideTranslocoPreloadLangs } from '@jsverse/transloco-preload-langs';
import { definePreset, palette } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import { provideCacheableAnimationLoader } from 'ngx-lottie';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { ENVIRONMENT, loadEnvironment } from '../environments/environment';
import { AuthenticationGuard } from '../guards/authentication.guard';
import { httpInterceptor } from '../interceptors/http.interceptor';
import { MissingTranskeyHandler } from '../interceptors/missing-transkey-handler';
import { AuthenticationService } from '../services/authentication.service';
import { AuthStateService } from '../services/authstate.service';
import { ConfirmDialogsService } from '../services/confirm-dialogs.service';
import { NavbarService } from '../services/navbar.service';
import { TranslationsLoadedService } from '../services/translations-loaded.service';
import { WebsocketService } from '../services/websocket.service';
import { routes } from './app.routes';
import { TranslocoHttpLoader } from './transloco-loader';


export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHttpClient(withInterceptors([httpInterceptor])),
    provideAnimationsAsync(),
    {
      provide: APP_INITIALIZER,
      useFactory: (http: HttpClient, tl: TranslationsLoadedService) => () => loadEnvironment(http, tl),
      deps: [HttpClient, TranslationsLoadedService],
      multi: true,
    },
    {
      provide: 'ENVIRONMENT',
      useValue: ENVIRONMENT,
    },
    provideTranslocoPreloadLangs(['it']),
    provideTransloco({
      config: {
        availableLangs: ['it'],
        defaultLang: 'it',
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: false,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader
    }),
    provideTranslocoLocale(),
    provideTranslocoMessageformat(),
    { provide: TRANSLOCO_MISSING_HANDLER, useClass: MissingTranskeyHandler },
    provideRouter(routes),
    providePrimeNG({
      theme: {
        preset: definePreset(Aura, {
          semantic: {
            // primary: palette("#87ccca"),
            primary: {
              50: '{lime.50}',
              100: '{lime.100}',
              200: '{lime.200}',
              300: '{lime.300}',
              400: '{lime.400}',
              500: '{lime.500}',
              600: '{lime.600}',
              700: '{lime.700}',
              800: '{lime.800}',
              900: '{lime.900}',
              950: '{lime.950}'
            }
          },
          components: {
            dialog: {
              header: {
                padding: '0.75rem 1.25rem 1rem',
              },
              footer: {
                padding: '0 1.25rem 1rem 1.25rem',
              },
            },
            inputtext: {
              border: {
                radius: "0.75rem"
              }
            },
            inputgroup: {
              addon: {
                border: {
                  radius: "0.75rem"
                }
              }
            },
            textarea: {
              border: {
                radius: "0.75rem"
              }
            },
            button: {
              border: {
                radius: "0.75rem"
              }
            },
            multiselect: {
              border: {
                radius: "0.75rem"
              },
            },
            select: {
              border: {
                radius: "0.75rem"
              },
            }
          }
        }),
        options: {
          darkModeSelector: '.dark-theme'
        },
      },
      ripple: true,
    }),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    }),
    provideCacheableAnimationLoader(),
    AuthenticationService,
    AuthStateService,
    AuthenticationGuard,
    MessageService,
    ConfirmationService,
    ConfirmDialogsService,
    NavbarService,
    WebsocketService,
  ],
};